import { ReactNode } from "react";
import { DropDownData, IdNameList } from "../../../api/services/common/models";
import { MobileNumber } from "../../../api/services/nisParticipants/models";
import {
  LabelValue,
  UserBasicDetails,
  UserDetails,
} from "../../../helpers/types/common";

export interface ProgramSearchProgramFormProps {
  searchType: string;
  resetForm?: boolean;
  // individual
  participantFirstName: string;
  participantFirstNameFilter: string;
  participantLastName: string;
  participantLastNameFilter: string;
  keyNumber: string;
  keyNumberFilter: string;
  participantEmail: string;
  participantEmailFilter: string;
  isPrimaryParticipantEmail: boolean;
  nextParticipantEmail: string;
  nextParticipantEmailFilter: string;
  isPrimaryNEXTParticipantEmail: boolean;
  mobileISOCode: string;
  mobileCountryCode: string;
  participantMobileNumber: string;
  isPrimaryParticipantMobileNumber: boolean;
  clientName: string;
  clientNameFilter: string;
  clientManagedStatus: string;

  // program
  programStatus: string;
  programName: string;
  programNameFilter: string;
  programStartMonthAndYear: string;
  billingContactLastName: string;
  billingContactLastNameFilter: string;
  projectKey: string;
  projectKeyFilter: string;
  projectName: string;
  projectNameFilter: string;
  hrContactLastName: string;
  hrContactLastNameFilter: string;
  projectManager: string;
  lineOfBusiness: string;
  solutionCategory: string;
  clientRelationshipOwner: string;

  // generic participant
  genericAccountName: string;
  genericAccountNameFilter: string;
  genericRegistrationCode: string | null;
  genericRegistrationCodeFilter: string;
  nextView: string;
  genericAccountStatus: string;
  nextProgramClass: string;
  participantStatus: string;
  purpose: string;
  country: string;
  deliveryOffice: string;
  requestedBy: string;

  // NIS Participant
  nisId: string;
  nisIdFilter: string;
  clientEmployer: string;
  clientEmployerFilter: string;
  nisStatus: string;

  // More fields for NIS participant
  hrContact: string;
  hrContactFilter: string;
  participantEmailPersonal: string;
  participantEmailPersonalFilter: string;
  isParticipantEmailPersonal: boolean;
  nisMobileISOCode: string;
  nisMobileCountryCode: string;
  nisParticipantMobileNumber: string;
}

export const ProgramSearchProgramFormKey = {
  searchType: "searchType",
  resetForm: "resetForm",
  // individual
  participantFirstName: "participantFirstName",
  participantFirstNameFilter: "participantFirstNameFilter",
  participantLastName: "participantLastName",
  participantLastNameFilter: "participantLastNameFilter",
  keyNumber: "keyNumber",
  keyNumberFilter: "keyNumberFilter",
  participantEmail: "participantEmail",
  participantEmailFilter: "participantEmailFilter",
  isPrimaryParticipantEmail: "isPrimaryParticipantEmail",
  mobileISOCode: "mobileISOCode",
  mobileCountryCode: "mobileCountryCode",
  participantMobileNumber: "participantMobileNumber",
  isPrimaryParticipantMobileNumber: "isPrimaryParticipantMobileNumber",
  clientName: "clientName",
  clientNameFilter: "clientNameFilter",
  clientManagedStatus: "clientManagedStatus",
  nextParticipantEmail: "nextParticipantEmail",
  nextParticipantEmailFilter: "nextParticipantEmailFilter",
  isPrimaryNEXTParticipantEmail: "isPrimaryNEXTParticipantEmail",

  // NIS Participant
  nisId: "nisId",
  nisIdFilter: "nisIdFilter",
  clientEmployer: "clientEmployer",
  clientEmployerFilter: "clientEmployerFilter",
  nisStatus: "nisStatus",

  // This fields are common from program, generic-participant and nis participant, so it is defined in common section
  // participantFirstName: "participantFirstName",
  // participantFirstNameFilter: "participantFirstNameFilter",
  // participantLastName: "participantLastName",
  // participantLastNameFilter: "participantLastNameFilter",
  // projectManager: "projectManager",
  // country: "country",

  // More fields for NIS participant
  hrContact: "hrContact",
  hrContactFilter: "hrContactFilter",
  participantEmailPersonal: "participantEmailPersonal",
  participantEmailPersonalFilter: "participantEmailPersonalFilter",
  isParticipantEmailPersonal: "isParticipantEmailPersonal",
  nisMobileISOCode: "nisMobileISOCode",
  nisMobileCountryCode: "nisMobileCountryCode",
  nisParticipantMobileNumber: "nisParticipantMobileNumber",

  // END NIS Participant

  // program
  programStatus: "programStatus",
  programName: "programName",
  programNameFilter: "programNameFilter",
  programStartMonthAndYear: "programStartMonthAndYear",
  billingContactLastName: "billingContactLastName",
  billingContactLastNameFilter: "billingContactLastNameFilter",
  hrContactLastName: "hrContactLastName",
  hrContactLastNameFilter: "hrContactLastNameFilter",
  projectKey: "projectKey",
  projectKeyFilter: "projectKeyFilter",
  projectName: "projectName",
  projectNameFilter: "projectNameFilter",
  projectManager: "projectManager",
  lineOfBusiness: "lineOfBusiness",
  solutionCategory: "solutionCategory",
  clientRelationshipOwner: "clientRelationshipOwner",

  // generic participant
  genericAccountName: "genericAccountName",
  genericAccountNameFilter: "genericAccountNameFilter",
  genericRegistrationCode: "genericRegistrationCode",
  genericRegistrationCodeFilter: "genericRegistrationCodeFilter",
  nextView: "nextView",
  genericAccountStatus: "genericAccountStatus",
  nextProgramClass: "nextProgramClass",
  participantStatus: "participantStatus",
  purpose: "purpose",
  country: "country",
  deliveryOffice: "deliveryOffice",
  requestedBy: "requestedBy",
} as const;

export const NISParticipantStatus = {
  Open: "Status.Open",
  UnableToValidate: "Status.UnableToValidate",
  Converted: "Status.Converted",
  Ineligible: "Status.Ineligible",
  CreatedInError: "Status.CreatedInError",
} as const;

export const searchTypeKey = {
  all: "all",
  nisParticipants: "nisParticipants",
  individuals: "individuals",
  programs: "programs",
  genericParticipant: "genericParticipant",
};
export const transferTypeKey = {
  changeCountry: "changeCountry",
  changeTransfer: "changeTransfer",
};
export interface TransferParticipantFormKeyProps {
  transferType?: string;
  destinationCountry: string;
  compatibleProgramTemplate: string;
}

export const TransferParticipantFormKey = {
  transferType: "transferType",
  destinationCountry: "destinationCountry",
  compatibleProgramTemplate: "compatibleProgramTemplate",
  nextLanguage: "nextLanguage",
};

export interface ProgramSearchData {
  dedicatedProjectManagerData: UserDetails[];
  isDedicatedProjectManagerApiSuccess: boolean;
  lineOfBusinessData: IdNameList[];
  solutionCategories: IdNameList[];
  solutionOfferingsData: IdNameList[];
  countryListData: DropDownData[];
  requestedByListData: UserDetails[];
  clientRelationshipOwnerData: UserDetails[];
  isClientRelationshipOwnerApiSuccess: boolean;
  isValidForm: boolean;
  handleSearchClick: () => void;
  showMore: boolean;
  setShowMore: (value: boolean) => void;
  setSearchResultList: (value: ProgramSearchResultRowValue[]) => void;
  setIsSearchClick: (value: boolean) => void;
}

export interface ProgramSearchDataProviderProps {
  children: ReactNode;
  apiData: ProgramSearchData;
}

export interface ProgramSearchResultRowValue {
  id: string;
  programName?: string;
  keyNumber: string;
  participantName?: string;
  programStatus: string;
  companyName: string;
  programTemplateName?: string;
  country: string;
  countryName?: string;
  createdDate: string;
  candidateType?: string;
  clientManagedStatus?: string;
  participantEmail?: string;
  participantMobileNumber?: string;
  projectManager?: string;
  billingContactName?: string;
  hrContactName?: string;
  lineOfBusiness: string;
  localClientRelationShipOwner?: string;
  clientRelationShipOwner?: string;
  solutionCategory: string;
  programStartDate?: string;
  solutionOfferings?: string;
  statusKey?: string;
  projectKey?: string;
  projectName?: string;
  [key: string]: any;
}

export interface ProgramGenericAccountSearchParticipantsResultRowValue {
  id: string;
  participantName?: string;
  participantFirstName: string;
  participantLastName: string;
  participantEmail: string;
  programEndDate: string;
  participantStatus: string;
  deliveryOffice: string;
  statusKey?: string;
  [key: string]: any;
}

export interface ProgramNISParticipantsSearchResultRowValue {
  participantFirstName: string;
  participantLastName: string;
  programName?: string;
  nisNumber: IdNameList;
  programTemplate?: IdNameList;
  participantType: string;
  participantStatus: string;
  client: IdNameList;
  clientManagedStatus?: string;
  participantEmail: string;
  participantMobileNumber?: MobileNumber;
  country: IdNameList;
  createdDate: string;
  programBillingContact?: UserBasicDetails;
  projectManager: UserBasicDetails;
  hrContact?: UserBasicDetails;
  localClientRelationshipOwner?: UserBasicDetails;
  productLine?: IdNameList;
  solutionCategory?: string;
  programStartDate?: string;
  [key: string]: any;
}

export interface ProgramNISParticipantsSearchResultFormType {
  participantName: string;
  programName: string;
  nisNumber: string;
  programTemplate: string;
  participantType: string;
  participantStatus: string;
  clientName: string;
  clientManagedStatus: string;
  participantEmail: string;
  participantMobileNumber: string;
  country: string[];
  createdDate: string;
  programBillingContact: string;
  projectManager: string;
  hrContact: string;
  localClientRelationshipOwner: string;
  productLine: string;
  solutionCategory: string;
  programStartDate: string;
  [key: string]: any;
}

export const SearchProgramResultFormKey = {
  participantName: "participantName",
  programName: "programName",
  keyNumber: "keyNumber",
  participantType: "participantType",
  programStatus: "programStatus",
  participantStatus: "participantStatus",
  companyName: "companyName",
  clientManagedStatus: "clientManagedStatus",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  participantMobileNumberDisplay: "participantMobileNumberDisplay",
  projectManager: "projectManager",
  projectManagerName: "projectManagerName",
  country: "country",
  countryName: "countryName",
  createdDate: "createdDate",
  billingContactName: "billingContactName",
  hrContactName: "hrContactName",
  lineOfBusiness: "lineOfBusiness",
  clientRelationShipOwner: "clientRelationShipOwner",
  solutionCategory: "solutionCategory",
  programStartDate: "programStartDate",
  solutionOffering: "solutionOffering",
  programEndDate: "programEndDate",
  deliveryOfficeId: "deliveryOfficeId",
  deliveryOffice: "deliveryOffice",
  projectKey: "projectKey",
  projectName: "projectName",
  filterCountryName: "filterCountryName",
  nisNumber: "nisNumber",
  nisNumberName: "nisNumberName",
  programTemplate: "programTemplate",
  programTemplateName: "programTemplateName",
};

export interface UniqueProgramSearchResultFiltersTypes {
  programStatus: LabelValue[];
  clientManagedStatus: LabelValue[];
  solutionCategory: LabelValue[];
  lineOfBusiness: LabelValue[];
}

export interface UniqueProgramSearchNISParticipantsResultFiltersTypes
  extends Omit<UniqueProgramSearchResultFiltersTypes, "programStatus"> {
  participantStatus: LabelValue[];
}

export interface DeliveryOfficeResponse {
  rightOfficeId: string;
  rightOfficeName: string;
  countryCode: string;
}

export interface ProgramSearchResultProps {
  rows: ProgramSearchResultRowValue[];
  isSearch: boolean;
  pageId: string;
  searchType: string;
}

export interface NISParticipantSearchResultProps {
  rows: ProgramNISParticipantsSearchResultRowValue[];
  isSearch: boolean;
  pageId: string;
  searchType: string;
}

export interface GenericParticipantSearchResultsProps {
  rows: ProgramGenericAccountSearchParticipantsResultRowValue[];
  isSearch: boolean;
  pageId: string;
}

export interface UniqueGenericParticipantResultFiltersTypes {
  participantStatus: LabelValue[];
  deliveryOfficeName: LabelValue[];
  participantEmail: LabelValue[];
}
